import { useEffect, useState } from "react";
import { toast } from "../components/ui/sonner";
import { ListChecks, ArrowsClockwise } from "@phosphor-icons/react";
import { EmptyState } from "../components/shared/EmptyState";
import { getFeatureCoverage, buildFeatureSnapshotsForDate } from "../lib/api";
import { SEGMENTS } from "../constants/segments";

const LEAGUES = ["MLB", "NPB", "LMB", "CPB"];
const CATEGORY_KEYS = [
  "pitcher",
  "offense",
  "rest",
  "bullpen",
  "lineup",
  "park",
  "weather",
  "opponent_adjusted",
  "league_context",
  "recent_form",
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const DOT_COLOR = { available: "bg-emerald-400", error: "bg-amber-400" };
const AUDIT_PILL = {
  available: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
  partial: "border-yellow-500/40 bg-yellow-500/10 text-yellow-400",
  unavailable: "border-red-500/40 bg-red-500/10 text-red-400",
  error: "border-amber-500/40 bg-amber-500/10 text-amber-400",
};

const FeatureCoverage = () => {
  const [league, setLeague] = useState("MLB");
  const [date, setDate] = useState(todayIso());
  const [segment, setSegment] = useState("FULL_GAME");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);

  const load = () => {
    setLoading(true);
    getFeatureCoverage({ league, date, segment })
      .then((data) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league, date, segment]);

  const build = async () => {
    setBuilding(true);
    try {
      const res = await buildFeatureSnapshotsForDate({ league, date, segment });
      toast.success(`Built ${res.built}/${res.games_found} snapshots for ${league} on ${date}`);
      load();
    } catch (e) {
      toast.error(`Snapshot build failed for ${league}`);
    } finally {
      setBuilding(false);
    }
  };

  return (
    <div data-testid="feature-coverage-page" className="mx-auto max-w-[1200px] px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-center gap-2">
        <ListChecks size={22} className="text-eb-accent" />
        <h1 className="font-heading text-3xl uppercase tracking-tight text-white">Feature Coverage</h1>
      </div>
      <p className="mb-6 max-w-2xl text-xs text-zinc-500">
        Read-only view of the Phase 3 feature-engineering foundation: whether real, timestamp-safe data exists per
        category for each game. No predictions, probabilities or odds are computed anywhere on this page.
        Feature version: <span className="text-zinc-300">v1-foundation</span>
      </p>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div className="flex gap-1.5">
          {LEAGUES.map((lg) => (
            <button
              key={lg}
              data-testid={`feature-coverage-league-${lg.toLowerCase()}`}
              onClick={() => setLeague(lg)}
              className={`rounded-md px-3 py-1.5 font-mono text-xs uppercase tracking-wide transition-colors ${
                league === lg
                  ? "border border-eb-accent bg-eb-accent/10 text-white"
                  : "border border-white/10 text-zinc-400 hover:border-white/30"
              }`}
            >
              {lg}
            </button>
          ))}
        </div>
        <input
          type="date"
          data-testid="feature-coverage-date-input"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="rounded-md border border-white/10 bg-eb-surface px-3 py-1.5 font-mono text-xs text-white"
        />
        <select
          data-testid="feature-coverage-segment-select"
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
          className="rounded-md border border-white/10 bg-eb-surface px-3 py-1.5 font-mono text-xs uppercase text-white"
        >
          {SEGMENTS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <button
          data-testid="feature-coverage-build-button"
          disabled={building}
          onClick={build}
          className="flex items-center gap-1.5 rounded-md border border-eb-accent/40 bg-eb-accent/10 px-3 py-1.5 font-mono text-xs uppercase text-white transition-colors hover:border-eb-accent disabled:opacity-50"
        >
          <ArrowsClockwise size={13} /> Build Snapshots For Date
        </button>
      </div>

      <div data-testid="feature-coverage-legend" className="mb-4 flex items-center gap-4 font-mono text-[11px] text-zinc-500">
        <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-emerald-400" /> Available</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-red-400" /> Unavailable</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-amber-400" /> Error</span>
        <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-zinc-700" /> Not built yet</span>
      </div>

      {loading ? (
        <div className="rounded-lg border border-white/10 bg-eb-surface p-10 text-center font-mono text-xs text-zinc-500">
          Loading coverage&hellip;
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No Games Found"
          description="No ingested games for this league/date yet. Import games from Data Status first."
          testId="feature-coverage-empty"
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-white/10 bg-eb-surface" data-testid="feature-coverage-table">
          <table className="w-full text-left font-mono text-xs">
            <thead>
              <tr className="border-b border-white/10 text-zinc-500">
                <th className="px-4 py-3">Matchup</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Audit</th>
                {CATEGORY_KEYS.map((k) => (
                  <th key={k} className="px-2 py-3 uppercase" title={k}>
                    {k.slice(0, 4)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.game_id}
                  data-testid={`feature-coverage-row-${r.game_id}`}
                  className="border-b border-white/5 text-zinc-300"
                >
                  <td className="px-4 py-3">
                    {r.away_team || "?"} @ {r.home_team || "?"}
                  </td>
                  <td className="px-4 py-3">{r.status}</td>
                  <td className="px-4 py-3">
                    {r.audit_available ? (
                      <span className="flex items-center gap-1.5">
                        <span
                          data-testid={`feature-coverage-audit-yes-${r.game_id}`}
                          className={`rounded-full border px-2 py-0.5 text-[10px] uppercase ${AUDIT_PILL[r.audit_status] || AUDIT_PILL.unavailable}`}
                        >
                          {r.audit_status}
                        </span>
                        {r.is_stale && (
                          <span data-testid={`feature-coverage-stale-${r.game_id}`} className="rounded-full border border-yellow-500/40 bg-yellow-500/10 px-2 py-0.5 text-[10px] uppercase text-yellow-400">
                            Stale
                          </span>
                        )}
                      </span>
                    ) : (
                      <span data-testid={`feature-coverage-audit-no-${r.game_id}`} className="text-zinc-500">
                        Not built
                      </span>
                    )}
                  </td>
                  {CATEGORY_KEYS.map((k) => {
                    const status = r.availability ? r.availability[k] : null;
                    const reason = r.notes ? r.notes[k] : null;
                    return (
                      <td key={k} className="px-2 py-3 text-center">
                        <span
                          data-testid={`feature-coverage-dot-${r.game_id}-${k}`}
                          title={status === null ? "not built" : reason ? `${status}: ${reason}` : status}
                          className={`inline-block h-2 w-2 rounded-full ${
                            status === null ? "bg-zinc-700" : DOT_COLOR[status] || "bg-red-400"
                          }`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FeatureCoverage;
